// @ts-nocheck
import React, { useState } from 'react';
import { Box, CircularProgress, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import { parseFieldValue, toInputValue } from './editableField.helpers';

const BOOLEAN_OPTIONS = [{ id: 'true', text: 'Ja' }, { id: 'false', text: 'Nej' }];

// Shows a value with a pencil next to it. Clicking the pencil turns the value into an input.
// type: 'text' | 'number' | 'date' | 'select' | 'boolean'. options (for select): [{ id, text }].
// storedType: the type the value is stored as when it is new ('string' or 'number').
// onSave(newValue) must return a promise and throw if saving failed.
function EditableField({ value, type = 'text', options = [], storedType, onSave, children, label }) {
    const [editing, setEditing] = useState(false);
    const [raw, setRaw] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);

    const startEditing = () => {
        setRaw(toInputValue(type, value));
        setError(false);
        setEditing(true);
    };

    const save = async () => {
        const nextValue = parseFieldValue({ type, raw, previous: value, options, storedType });
        if (nextValue === undefined) {
            setError(true);
            return;
        }
        if (nextValue === value) {
            setEditing(false);
            return;
        }

        setSaving(true);
        try {
            await onSave(nextValue);
            setEditing(false);
        } catch (saveError) {
            setError(true);
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover .editable-field-pencil': { opacity: 1 } }}>
                <span>{ children === undefined || children === null || children === '' ? '-' : children }</span>
                <Tooltip title={ label ? `Rediger ${label.toLowerCase()}` : 'Rediger' }>
                    <IconButton
                        className="editable-field-pencil"
                        size="small"
                        onClick={ startEditing }
                        aria-label={ label ? `Rediger ${label}` : 'Rediger' }
                        sx={{ opacity: 0.4, displayPrint: 'none' }}
                    >
                        <EditOutlinedIcon fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    }

    const selectOptions = type === 'boolean' ? BOOLEAN_OPTIONS : options;
    const isSelect = type === 'select' || type === 'boolean';

    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <TextField
                size="small"
                variant="standard"
                autoFocus
                select={ isSelect }
                type={ type === 'date' ? 'date' : 'text' }
                inputProps={{ inputMode: type === 'number' ? 'decimal' : undefined, 'aria-label': label }}
                SelectProps={{ SelectDisplayProps: { 'aria-label': label } }}
                value={ raw }
                error={ error }
                helperText={ error ? 'Kunne ikke gemme' : undefined }
                disabled={ saving }
                onChange={ e => setRaw(e.target.value) }
                onKeyDown={ (e) => {
                    if (e.key === 'Enter' && !isSelect) save();
                    if (e.key === 'Escape') setEditing(false);
                } }
                sx={{ minWidth: 160 }}
            >
                { isSelect && selectOptions.map(option => (
                    <MenuItem key={ String(option.id) } value={ String(option.id) }>{ option.text }</MenuItem>
                )) }
            </TextField>
            { saving ? <CircularProgress size={ 18 } /> : (
                <>
                    <IconButton size="small" color="primary" onClick={ save } aria-label="Gem">
                        <CheckIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton size="small" onClick={ () => setEditing(false) } aria-label="Fortryd">
                        <CloseIcon fontSize="inherit" />
                    </IconButton>
                </>
            ) }
        </Box>
    );
}

export default EditableField;
